import AttendanceBadge from './AttendanceBadge.tsx';
import type { AttendanceSummary } from './useContactAttendanceSummary.ts';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Cpu,
  Loader2,
  User,
  X,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import {
  getContactMode,
  isContactModeTransitionAmbiguous,
  isContactModeTransitionRetryable,
  parseApiError,
  setContactMode,
} from '../../services/api.ts';
import type { ContactModeResponse } from '../../services/api.ts';

export interface ContactModeVersion {
  auditId?: number;
  changedAt?: number;
}

export type ContactModeEventDisposition = 'apply' | 'reconcile' | 'ignore';

export const classifyContactModeEvent = (
  event: any,
  clinicId: number,
  contactId: number,
): ContactModeEventDisposition => {
  const rawClinicId = event?.clinic_id ?? event?.data?.clinic_id;
  const rawContactId = event?.contact_id ?? event?.data?.contact_id;
  const hasClinicId = rawClinicId !== undefined && rawClinicId !== null && rawClinicId !== '';
  const hasContactId = rawContactId !== undefined && rawContactId !== null && rawContactId !== '';
  const eventClinicId = Number(rawClinicId);
  const eventContactId = Number(rawContactId);

  if (
    (hasClinicId &&
      (!Number.isInteger(eventClinicId) ||
        eventClinicId <= 0 ||
        eventClinicId !== clinicId)) ||
    (hasContactId &&
      (!Number.isInteger(eventContactId) ||
        eventContactId <= 0 ||
        eventContactId !== contactId))
  ) {
    return 'ignore';
  }

  if (!hasClinicId || !hasContactId) {
    return 'reconcile';
  }

  const rawAuditId = event?.audit_id ?? event?.data?.audit_id;
  const rawChangedAt = event?.changed_at ?? event?.data?.changed_at;
  const auditId = Number(rawAuditId);
  const changedAt = rawChangedAt ? new Date(rawChangedAt).getTime() : NaN;
  const hasVersion =
    (Number.isInteger(auditId) && auditId > 0) ||
    Number.isFinite(changedAt);

  return hasVersion ? 'apply' : 'reconcile';
};

export const registerContactModeVersion = (
  versions: Map<number, ContactModeVersion>,
  contactId: number,
  auditId?: number | null,
  changedAt?: string | null,
  options: { allowEqual?: boolean } = {},
): boolean => {
  const incomingAuditId =
    Number.isInteger(Number(auditId)) && Number(auditId) > 0
      ? Number(auditId)
      : undefined;
  const parsedChangedAt = changedAt ? new Date(changedAt).getTime() : NaN;
  const incomingChangedAt = Number.isFinite(parsedChangedAt)
    ? parsedChangedAt
    : undefined;
  const previous = versions.get(contactId);

  if (
    previous &&
    incomingAuditId === undefined &&
    incomingChangedAt === undefined
  ) {
    return false;
  }

  if (
    incomingAuditId !== undefined &&
    previous?.auditId !== undefined &&
    (
      incomingAuditId < previous.auditId ||
      (incomingAuditId === previous.auditId && !options.allowEqual)
    )
  ) {
    return false;
  }
  if (
    incomingAuditId === undefined &&
    incomingChangedAt !== undefined &&
    previous?.changedAt !== undefined &&
    (
      incomingChangedAt < previous.changedAt ||
      (incomingChangedAt === previous.changedAt && !options.allowEqual)
    )
  ) {
    return false;
  }

  if (incomingAuditId !== undefined || incomingChangedAt !== undefined) {
    versions.set(contactId, {
      auditId: incomingAuditId ?? previous?.auditId,
      changedAt:
        incomingChangedAt !== undefined && previous?.changedAt !== undefined
          ? Math.max(incomingChangedAt, previous.changedAt)
          : incomingChangedAt ?? previous?.changedAt,
    });
  }
  return true;
};

export const resolvePolledContactMode = (
  existing: { id?: number; human_mode?: boolean } | undefined,
  incoming: { id?: number; human_mode?: boolean },
): { humanMode: boolean; requiresReconciliation: boolean } => {
  const incomingMode = incoming.human_mode === true;
  if (!existing || Number(existing.id) !== Number(incoming.id)) {
    return {
      humanMode: incomingMode,
      requiresReconciliation: false,
    };
  }

  const existingMode = existing.human_mode === true;
  if (typeof incoming.human_mode !== 'boolean') {
    return {
      humanMode: existingMode,
      requiresReconciliation: false,
    };
  }
  return {
    humanMode: existingMode,
    requiresReconciliation: existingMode !== incomingMode,
  };
};

interface ContactModeCarrier {
  id?: number;
  human_mode?: boolean;
}

export const withKnownContactMode = <T extends ContactModeCarrier>(
  contact: T,
  knownModes: ReadonlyMap<number, boolean>,
): T => {
  const contactId = Number(contact.id);
  if (!Number.isInteger(contactId) || contactId <= 0) return contact;

  const knownMode = knownModes.get(contactId);
  if (knownMode === undefined || contact.human_mode === knownMode) return contact;

  return { ...contact, human_mode: knownMode };
};

export const applyContactModeToCollection = <T extends ContactModeCarrier>(
  contacts: T[],
  contactId: number,
  humanMode: boolean,
): T[] => {
  if (!Number.isInteger(contactId) || contactId <= 0) return contacts;

  let changed = false;
  const next = contacts.map((contact) => {
    if (Number(contact.id) !== contactId || contact.human_mode === humanMode) {
      return contact;
    }
    changed = true;
    return { ...contact, human_mode: humanMode };
  });
  return changed ? next : contacts;
};

interface ContactModeControlProps {
  attendanceSummary?: AttendanceSummary | null;
  contactId: number | null | undefined;
  humanMode: boolean;
  identityResolved: boolean;
  onModeConfirmed: (response: ContactModeResponse) => boolean | void;
  contactLabel?: string;
  variant?: 'compact' | 'panel';
  onBusyChange?: (isBusy: boolean) => void;
  className?: string;
  returnOnly?: boolean;
  onAssume?: () => void;
}

interface Feedback {
  contactId: number;
  type: 'success' | 'error';
  message: string;
}

type ContactModeStage =
  | 'idle'
  | 'confirming'
  | 'pending'
  | 'reconciling'
  | 'success'
  | 'error';

interface TransitionTarget {
  contactId: number;
  expectedHumanMode: boolean;
  desiredHumanMode: boolean;
  contactLabel: string;
}

const isResponseForContact = (
  response: ContactModeResponse,
  contactId: number,
): boolean => Number(response?.contact_id) === contactId;

const ContactModeControl: React.FC<ContactModeControlProps> = ({
  contactId,
  humanMode,
  identityResolved,
  onModeConfirmed,
  contactLabel,
  variant = 'compact',
  attendanceSummary = null,
  onBusyChange,
  className = '',
  returnOnly = false,
  onAssume,
}) => {
  const { isDark } = useTheme();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const pendingContactIdsRef = useRef<Set<number>>(new Set());
  const currentContactIdRef = useRef<number | null>(null);
  const onModeConfirmedRef = useRef(onModeConfirmed);
  const [pendingContactIds, setPendingContactIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [syncingContactId, setSyncingContactId] = useState<number | null>(null);
  const [transitionTarget, setTransitionTarget] = useState<TransitionTarget | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [receipt, setReceipt] = useState<ContactModeResponse | null>(null);
  const [stage, setStage] = useState<ContactModeStage>('idle');

  const safeContactId =
    Number.isInteger(Number(contactId)) && Number(contactId) > 0
      ? Number(contactId)
      : null;
  const isPending = safeContactId !== null && pendingContactIds.has(safeContactId);
  const isSyncing = safeContactId !== null && syncingContactId === safeContactId;
  const isDisabled = !identityResolved || safeContactId === null || isPending || isSyncing;
  const dialogOpen =
    transitionTarget !== null && transitionTarget.contactId === safeContactId;

  useEffect(() => {
    onModeConfirmedRef.current = onModeConfirmed;
  }, [onModeConfirmed]);

  useEffect(() => {
    setReceipt((current) =>
      current && current.human_mode === humanMode ? current : null
    );
  }, [humanMode]);

  useEffect(() => {
    currentContactIdRef.current = safeContactId;
    setTransitionTarget(null);
    setReceipt((current) =>
      current && Number(current.contact_id) === safeContactId ? current : null
    );
    setFeedback((current) =>
      current?.contactId === safeContactId ? current : null
    );
    setStage(
      safeContactId !== null && pendingContactIdsRef.current.has(safeContactId)
        ? 'pending'
        : 'idle',
    );
  }, [safeContactId]);

  useEffect(() => {
    if (!dialogOpen) return;

    const previousActiveElement = document.activeElement as HTMLElement | null;
    cancelRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setTransitionTarget(null);
        setStage('idle');
        window.setTimeout(() => triggerRef.current?.focus(), 0);
        return;
      }

      if (event.key === 'Tab') {
        const focusableElements = Array.from(
          dialogRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ) || [],
        );
        if (focusableElements.length === 0) {
          event.preventDefault();
          return;
        }

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousActiveElement?.focus?.();
    };
  }, [dialogOpen]);

  useEffect(() => {
    onBusyChange?.(isPending);
  }, [isPending, onBusyChange]);

  useEffect(() => {
    if (
      variant !== 'panel' ||
      !identityResolved ||
      safeContactId === null ||
      pendingContactIdsRef.current.has(safeContactId)
    ) {
      return;
    }

    let cancelled = false;
    const operationContactId = safeContactId;
    setSyncingContactId(operationContactId);
    setStage('reconciling');

    void getContactMode(operationContactId)
      .then((response) => {
        if (
          cancelled ||
          currentContactIdRef.current !== operationContactId ||
          !isResponseForContact(response, operationContactId)
        ) {
          return;
        }
        const accepted = onModeConfirmedRef.current(response);
        if (accepted !== false) {
          setReceipt(response);
        }
      })
      .catch(() => {
        // A lista continua sendo um fallback seguro para exibição. A falta do
        // recibo só omite ator/horário; qualquer transição continuará exigindo
        // uma leitura autoritativa própria depois do PUT.
      })
      .finally(() => {
        if (!cancelled && currentContactIdRef.current === operationContactId) {
          setSyncingContactId(null);
          setStage('idle');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [humanMode, identityResolved, safeContactId, variant]);

  const publishFeedback = (
    operationContactId: number,
    nextFeedback: Omit<Feedback, 'contactId'>,
  ) => {
    if (currentContactIdRef.current === operationContactId) {
      setFeedback({ contactId: operationContactId, ...nextFeedback });
      setStage(nextFeedback.type);
    }
  };

  const confirmAuthoritativeState = (
    response: ContactModeResponse,
    operationContactId: number,
    desiredHumanMode: boolean,
    reconciled: boolean,
  ): boolean => {
    if (!isResponseForContact(response, operationContactId)) {
      publishFeedback(operationContactId, {
        type: 'error',
        message: 'O servidor respondeu para outro contato. O estado não foi alterado nesta tela.',
      });
      return false;
    }

    const accepted = onModeConfirmedRef.current(response);
    if (accepted === false) {
      publishFeedback(operationContactId, {
        type: 'error',
        message: 'Uma alteração mais recente já foi confirmada. A tela manteve o estado mais atual.',
      });
      return false;
    }
    setReceipt(response);

    if (response.human_mode !== desiredHumanMode) {
      publishFeedback(operationContactId, {
        type: 'error',
        message: desiredHumanMode
          ? 'O atendimento humano não foi confirmado. A tela foi sincronizada com o estado atual.'
          : 'A devolução para a IA não foi confirmada. A tela foi sincronizada com o estado atual.',
      });
      return false;
    }

    const actorName = response.audit?.actor_name?.trim();
    publishFeedback(operationContactId, {
      type: 'success',
      message: desiredHumanMode
        ? `Atendimento humano confirmado${actorName ? ` por ${actorName}` : ''}${reconciled ? ' após conferência do servidor' : ''}.`
        : `Atendimento devolvido para a IA${actorName ? ` por ${actorName}` : ''}${reconciled ? ' após conferência do servidor' : ''}.`,
    });
    return true;
  };

  const reconcileAfterCommand = async (
    operationContactId: number,
    desiredHumanMode: boolean,
  ): Promise<boolean> => {
    if (currentContactIdRef.current === operationContactId) {
      setStage('reconciling');
    }
    try {
      const authoritativeState = await getContactMode(operationContactId);
      return confirmAuthoritativeState(
        authoritativeState,
        operationContactId,
        desiredHumanMode,
        true,
      );
    } catch (verificationError) {
      const parsedVerificationError = parseApiError(
        verificationError,
        'Não foi possível conferir o estado atual do atendimento.',
      );
      publishFeedback(operationContactId, {
        type: 'error',
        message: `${parsedVerificationError.message} Atualize a conversa antes de tentar novamente.`,
      });
      return false;
    }
  };

  const performTransition = async (
    operationContactId: number,
    desiredHumanMode: boolean,
    expectedHumanMode: boolean,
  ) => {
    if (pendingContactIdsRef.current.has(operationContactId)) return;

    pendingContactIdsRef.current.add(operationContactId);
    setPendingContactIds((current) => {
      const next = new Set(current);
      next.add(operationContactId);
      return next;
    });
    if (currentContactIdRef.current === operationContactId) {
      setFeedback(null);
      setStage('pending');
    }

    try {
      let transitionError: unknown;
      let commandAccepted = false;
      try {
        await setContactMode(
          operationContactId,
          desiredHumanMode,
          expectedHumanMode,
        );
        commandAccepted = true;
      } catch (error) {
        transitionError = error;

      // A primeira chamada pode continuar executando no servidor mesmo após
      // timeout/queda de rede. Repetir o mesmo compare-and-set é seguro:
      // ou conclui a intenção ainda não aplicada, ou recebe 409 sem criar uma
      // segunda auditoria quando a primeira já confirmou a transição.
        if (isContactModeTransitionRetryable(error)) {
          try {
            await setContactMode(
              operationContactId,
              desiredHumanMode,
              expectedHumanMode,
            );
            commandAccepted = true;
          } catch (retryError) {
            transitionError = retryError;
          }
        }

        if (!commandAccepted && isContactModeTransitionAmbiguous(transitionError)) {
          await reconcileAfterCommand(operationContactId, desiredHumanMode);
          return;
        }

        if (!commandAccepted) {
          const parsedError = parseApiError(
            transitionError,
            desiredHumanMode
              ? 'Não foi possível puxar o atendimento.'
              : 'Não foi possível devolver o atendimento para a IA.',
          );
          publishFeedback(operationContactId, {
            type: 'error',
            message: parsedError.message,
          });
          return;
        }
      }

      if (commandAccepted) {
        await reconcileAfterCommand(operationContactId, desiredHumanMode);
      }
    } finally {
      pendingContactIdsRef.current.delete(operationContactId);
      setPendingContactIds((current) => {
        const next = new Set(current);
        next.delete(operationContactId);
        return next;
      });
    }
  };

  const handleClick = () => {
    if (isDisabled || safeContactId === null) return;
    // Sem responsável (onAssume), claim cobre legado humano e IA.
    if (onAssume) {
      onAssume();
      return;
    }
    setFeedback(null);
    setStage('confirming');
    setTransitionTarget({
      contactId: safeContactId,
      expectedHumanMode: humanMode,
      desiredHumanMode: !humanMode,
      contactLabel: contactLabel?.trim() || 'este contato',
    });
  };

  const closeDialog = () => {
    setTransitionTarget(null);
    setStage('idle');
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };

  const confirmTransition = () => {
    if (!transitionTarget || transitionTarget.contactId !== safeContactId) return;
    const target = transitionTarget;
    setTransitionTarget(null);
    void performTransition(
      target.contactId,
      target.desiredHumanMode,
      target.expectedHumanMode,
    );
  };

  const title = !identityResolved
    ? 'Aguardando identificação segura do contato'
    : onAssume
      ? 'Assumir atendimento'
      : humanMode
        ? 'Devolver atendimento para a IA'
        : 'Puxar atendimento para humano';

  const desiredHumanMode = transitionTarget?.desiredHumanMode === true;
  const dialogTitle = desiredHumanMode
    ? 'Puxar atendimento humano?'
    : 'Devolver atendimento para a IA?';
  const conservativeAction = desiredHumanMode
    ? 'Manter IA ativa'
    : 'Manter atendimento humano';
  const confirmAction = desiredHumanMode
    ? 'Puxar atendimento'
    : 'Devolver para IA';

  const formattedReceipt = (() => {
    if (
      !receipt ||
      Number(receipt.contact_id) !== safeContactId ||
      receipt.human_mode !== humanMode
    ) {
      return null;
    }
    const actor =
      receipt.audit?.actor_name?.trim() ||
      receipt.audit?.actor_email?.trim() ||
      (receipt.audit?.actor_kind === 'system' ? 'Sistema' : null);
    const rawDate = receipt.audit?.created_at || receipt.changed_at;
    let formattedDate: string | null = null;
    if (rawDate) {
      const parsed = new Date(rawDate);
      if (!Number.isNaN(parsed.getTime())) {
        formattedDate = new Intl.DateTimeFormat('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(parsed);
      }
    }
    if (!actor && !formattedDate) return null;
    return [actor, formattedDate].filter(Boolean).join(' · ');
  })();

  const dialog = dialogOpen && typeof document !== 'undefined'
    ? createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDialog();
          }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-mode-dialog-title"
            aria-describedby="contact-mode-dialog-description"
            data-contact-mode-dialog="true"
            className={`w-full max-w-md rounded-2xl border shadow-xl ${
              isDark
                ? 'border-gray-600 bg-gray-800 text-gray-200'
                : 'border-gray-200 bg-white text-gray-800'
            }`}
          >
            <div
              className={`flex items-center justify-between border-b px-5 py-4 ${
                isDark ? 'border-gray-700' : 'border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`rounded-xl p-2 ${
                    desiredHumanMode
                      ? isDark
                        ? 'bg-sminy-primary/20 text-sminy-primary'
                        : 'bg-sminy-primary/10 text-sminy-primary'
                      : isDark
                        ? 'bg-amber-900/30 text-amber-300'
                        : 'bg-amber-50 text-amber-600'
                  }`}
                >
                  {desiredHumanMode ? (
                    <User className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                  )}
                </div>
                <h2 id="contact-mode-dialog-title" className="text-base font-semibold">
                  {dialogTitle}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeDialog}
                className={`flex min-h-11 min-w-11 items-center justify-center rounded-xl transition-colors ${
                  isDark
                    ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
                aria-label="Fechar confirmação"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="px-5 py-5">
              <p
                id="contact-mode-dialog-description"
                className={`text-sm leading-6 ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                {desiredHumanMode ? (
                  <>
                    A IA será pausada para <strong>{transitionTarget?.contactLabel}</strong> e
                    só voltará após uma devolução explícita para a IA.
                  </>
                ) : (
                  <>
                    A IA voltará a responder <strong>{transitionTarget?.contactLabel}</strong>.
                    Confirme somente quando o atendimento humano estiver encerrado.
                  </>
                )}
              </p>
            </div>

            <div
              className={`flex flex-col-reverse gap-3 border-t px-5 py-4 sm:flex-row sm:justify-end ${
                isDark ? 'border-gray-700' : 'border-gray-200'
              }`}
            >
              <button
                ref={cancelRef}
                type="button"
                onClick={closeDialog}
                className={`min-h-11 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                  isDark
                    ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {conservativeAction}
              </button>
              <button
                type="button"
                onClick={confirmTransition}
                className="min-h-11 rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sminy-primary/90 focus:outline-none focus:ring-2 focus:ring-sminy-primary/40"
              >
                {confirmAction}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  const trigger = (
    <button
        ref={triggerRef}
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        role={variant === 'panel' ? 'switch' : undefined}
        aria-label={variant === 'panel' ? 'Atendimento humano' : title}
        aria-checked={variant === 'panel' ? humanMode : undefined}
        aria-pressed={variant === 'compact' ? humanMode : undefined}
        aria-busy={isPending || isSyncing}
        title={title}
        data-state={stage}
        className={`flex shrink-0 min-h-11 items-center justify-center rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-sminy-primary/40 ${
          variant === 'panel' ? 'min-w-[52px] px-1.5' : 'min-w-11'
        } ${
          isDisabled
            ? isDark
              ? 'cursor-not-allowed text-gray-600'
              : 'cursor-not-allowed text-gray-300'
            : isDark
              ? 'hover:bg-gray-700/60'
              : 'hover:bg-gray-100'
        } ${className}`}
      >
        {isPending || isSyncing ? (
          <Loader2
            className="h-5 w-5 animate-spin text-sminy-primary"
            aria-hidden="true"
          />
        ) : variant === 'panel' ? (
          <span
            aria-hidden="true"
            className={`relative inline-flex shrink-0 h-7 w-12 items-center rounded-full transition-colors ${
              humanMode ? 'bg-rose-500' : 'bg-sminy-primary'
            }`}
          >
            <span
              className={`absolute top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm transition-all ${
                humanMode ? 'left-6 text-rose-500' : 'left-1 text-sminy-primary'
              }`}
            >
              {humanMode ? (
                <User className="h-3.5 w-3.5" />
              ) : (
                <Cpu className="h-3.5 w-3.5" />
              )}
            </span>
          </span>
        ) : humanMode ? (
          <User className="h-5 w-5 text-rose-500" aria-hidden="true" />
        ) : (
          <Cpu className="h-5 w-5 text-sminy-primary" aria-hidden="true" />
        )}
      </button>
  );

  if (returnOnly && !humanMode) return null;

  return (
    <>
      {variant === 'panel' ? (
        <div
          className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-3 ${
            isDark
              ? 'border-gray-700 bg-gray-800/70'
              : 'border-gray-200 bg-white'
          }`}
        >
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              Atendimento
            </p>
            <div className="mt-1 min-w-0" aria-live="polite">
              <AttendanceBadge humanMode={humanMode} summary={attendanceSummary} />
            </div>
            {formattedReceipt && (
              <p className={`mt-1 truncate text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Última alteração: {formattedReceipt}
              </p>
            )}
          </div>
          {trigger}
        </div>
      ) : trigger}

      {feedback?.contactId === safeContactId && (
        <div
          role={feedback.type === 'error' ? 'alert' : 'status'}
          aria-live={feedback.type === 'error' ? 'assertive' : 'polite'}
          className={`fixed bottom-[calc(env(safe-area-inset-bottom)+1rem)] right-4 z-[10001] flex max-w-[calc(100vw-2rem)] items-start gap-2 rounded-xl border px-4 py-3 text-sm shadow-xl sm:max-w-sm ${
            feedback.type === 'error'
              ? isDark
                ? 'border-red-800 bg-red-950 text-red-200'
                : 'border-red-200 bg-red-50 text-red-800'
              : isDark
                ? 'border-emerald-800 bg-emerald-950 text-emerald-200'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800'
          }`}
        >
          {feedback.type === 'error' ? (
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          )}
          <span>{feedback.message}</span>
          <button
            type="button"
            onClick={() => setFeedback(null)}
            className="ml-1 flex min-h-6 min-w-6 items-center justify-center rounded-lg opacity-70 transition-opacity hover:opacity-100"
            aria-label="Fechar aviso"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {dialog}
    </>
  );
};

export default ContactModeControl;
